/**
 * Wanderlaut i18n setup.
 *
 * Master language is German — all UI strings are authored in de.json
 * first, the other five locales are generated from that master.
 * Browser locale is detected once at boot and persisted in
 * localStorage so the user's explicit choice survives refreshes.
 *
 * Public surface:
 *   - t(key, opts?)      — shorthand around i18next.t
 *   - getLang()          — current active 2-letter code
 *   - setLang(code)      — switch + persist + rebroadcast
 *   - applyDomTranslations() — re-applies [data-i18n] attrs on demand
 *   - LANG_LIST          — UI dropdown source of truth
 */
import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import de from './locales/de.json';
import en from './locales/en.json';
import it from './locales/it.json';
import fr from './locales/fr.json';
import zh from './locales/zh.json';
import es from './locales/es.json';

export type LangCode = 'de' | 'en' | 'it' | 'fr' | 'zh' | 'es';

export const LANG_LIST: Array<{ code: LangCode; label: string }> = [
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'English' },
  { code: 'it', label: 'Italiano' },
  { code: 'fr', label: 'Français' },
  { code: 'zh', label: '中文' },
  { code: 'es', label: 'Español' },
];

const LANG_STORAGE_KEY = 'wanderlaut-lang';

// Fire-and-forget init. All resources are bundled inline (no remote
// load), so the init promise resolves on the next microtask and the
// rest of the module proceeds with i18next already populated. We
// don't `await` because Vite/esbuild's top-level-await emit is more
// fragile than this trivial sync resolution path.
i18next
  .use(LanguageDetector)
  .init({
    resources: {
      de: { translation: de },
      en: { translation: en },
      it: { translation: it },
      fr: { translation: fr },
      zh: { translation: zh },
      es: { translation: es },
    },
    fallbackLng: 'de',
    supportedLngs: ['de', 'en', 'it', 'fr', 'zh', 'es'],
    // Strip region — "de-AT" → "de", "en-GB" → "en".
    load: 'languageOnly',
    nonExplicitSupportedLngs: true,
    detection: {
      // localStorage wins so explicit user choice trumps browser default.
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: LANG_STORAGE_KEY,
      caches: ['localStorage'],
    },
    interpolation: { escapeValue: false },
  })
  .then(() => {
    // Reflect chosen language on <html lang="..."> for a11y + SEO once
    // detection has actually settled.
    document.documentElement.setAttribute('lang', i18next.language || 'de');
  });

export function t(key: string, opts?: Record<string, unknown>): string {
  return i18next.t(key, opts) as string;
}

export function getLang(): LangCode {
  return (i18next.language?.slice(0, 2) || 'de') as LangCode;
}

export async function setLang(code: LangCode): Promise<void> {
  await i18next.changeLanguage(code);
  document.documentElement.setAttribute('lang', code);
  try { localStorage.setItem(LANG_STORAGE_KEY, code); } catch { /* ignore */ }
  applyDomTranslations();
  window.dispatchEvent(new CustomEvent('wanderlaut:lang-changed', { detail: { lang: code } }));
}

/**
 * Re-translate all elements carrying a `data-i18n="key"` attribute.
 * Use this for static HTML rendered from the template — anything
 * built dynamically in JS should call t() directly at render time.
 *
 * Supports two attribute conventions:
 *   data-i18n="..."        → sets textContent
 *   data-i18n-attr="title:settings.label,placeholder:onboarding.input"
 *                          → sets the named attribute(s)
 */
export function applyDomTranslations(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>('[data-i18n]').forEach((el) => {
    const key = el.getAttribute('data-i18n');
    if (!key) return;
    el.textContent = t(key);
  });
  root.querySelectorAll<HTMLElement>('[data-i18n-attr]').forEach((el) => {
    const spec = el.getAttribute('data-i18n-attr');
    if (!spec) return;
    spec.split(',').forEach((pair) => {
      const [attr, key] = pair.split(':').map((s) => s.trim());
      if (attr && key) el.setAttribute(attr, t(key));
    });
  });
}

export default i18next;
